import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useAuth } from "../context/AuthContext";
import { API } from "../constants";

const CATEGORIES = [
  { key: "order_updates", label: "Order Updates", description: "Order shipped, delivered, and tracking info" },
  { key: "promotions", label: "Promotions & Deals", description: "Grand opening offers, flash sales, store credit" },
  { key: "vip_offers", label: "VIP Exclusive Offers", description: "Early access, member-only deals, drops" },
];

const PushNotificationToggle = ({ compact = false }) => {
  const { token } = useAuth();
  const { supported, permission, subscribed, loading, subscribe, unsubscribe } = usePushNotifications(token);
  const [prefs, setPrefs] = useState({ order_updates: true, promotions: true, vip_offers: true });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token || !subscribed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/push/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setPrefs(res.data);
          setPrefsLoaded(true);
        }
      } catch {
        if (!cancelled) setPrefsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token, subscribed]);

  const savePrefs = async (next) => {
    setSaving(true);
    setPrefs(next);
    try {
      await axios.put(`${API}/push/preferences`, next, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (!supported) return null;
  if (permission === 'denied') return null;

  if (compact) {
    return (
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={loading}
        title={subscribed ? "Disable order notifications" : "Enable order notifications"}
        data-testid="push-toggle-compact"
        className={`flex items-center gap-1.5 text-xs transition-colors disabled:opacity-40 ${
          subscribed ? 'text-[#D4AF37]' : 'text-gray-500 hover:text-gray-300'
        }`}
      >
        {subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        {loading ? '...' : subscribed ? 'Alerts On' : 'Alerts Off'}
      </button>
    );
  }

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm" data-testid="push-notification-card">
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${subscribed ? 'bg-[#D4AF37]/20' : 'bg-white/5'}`}>
            {subscribed ? <Bell className="w-4 h-4 text-[#D4AF37]" /> : <BellOff className="w-4 h-4 text-gray-500" />}
          </div>
          <div>
            <p className="text-white text-sm font-semibold">Push Notifications</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {subscribed
                ? "Choose which notifications you want to receive"
                : "Get notified about orders, deals, and VIP drops"}
            </p>
          </div>
        </div>
        <button
          onClick={subscribed ? unsubscribe : subscribe}
          disabled={loading}
          data-testid="push-toggle-btn"
          className={`flex-shrink-0 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors disabled:opacity-40 ${
            subscribed
              ? 'border-red-500/30 text-red-400 hover:border-red-500 hover:bg-red-500/10'
              : 'border-[#D62828]/40 text-[#D62828] hover:border-[#D62828] hover:bg-[#D62828]/10'
          }`}
        >
          {loading ? '...' : subscribed ? 'Turn Off' : 'Enable'}
        </button>
      </div>

      {subscribed && prefsLoaded && (
        <div className="border-t border-white/5 px-4 py-3 space-y-2" data-testid="push-categories">
          {CATEGORIES.map((cat) => (
            <label
              key={cat.key}
              className="flex items-start gap-3 cursor-pointer py-1.5 hover:bg-white/5 rounded-sm px-2 -mx-2 transition-colors"
              data-testid={`push-cat-${cat.key}`}
            >
              <input
                type="checkbox"
                checked={!!prefs[cat.key]}
                disabled={saving}
                onChange={(e) => savePrefs({ ...prefs, [cat.key]: e.target.checked })}
                className="mt-1 w-4 h-4 rounded-sm accent-[#D62828]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{cat.label}</p>
                <p className="text-gray-500 text-xs">{cat.description}</p>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default PushNotificationToggle;
